/*
 * Carbisdale - FileManager
 *
 * Verwaltet File-Operationen:
 * - Neu (Editor leeren)
 * - Import (.txt, .md)
 * - Load (.cbl)
 * - Save (.cbl)
 * - Export (.md)
 */

import { FORMAT_VERSION } from './MetadataManager.js'
import { serializeParagraphRT } from './RichTextOps.js'
import { remapParagraphIds } from './persistence/paragraphUtils.js'
import { EditorCleanup } from './editor/EditorCleanup.js'

/**
 * FileManager-Klasse
 */
export class FileManager {
    /**
     * @param {EditorModel} model - Das Editor-Model
     * @param {MetadataManager} metadata - Der Metadata-Manager
     * @param {Function} showConfirmDialog - Callback für Bestätigungs-Dialog
     * @param {Function} showInfoDialog - Callback für Info-Dialog
     * @param {Object} [opts]
     * @param {Object} [opts.projectManager] - ProjectManager fuer Title-
     *   Adoption (Import/Load schreiben dann direkt in project.title, statt
     *   nur in metadata.title — Pane-Border/Tab/Dashboard bleiben konsistent).
     *   Der UI-Sync nach einem Rename laeuft zentral ueber den
     *   `projectManager.setOnRenamed(...)`-Hook (in main.js verdrahtet).
     */
    constructor(model, metadata, showConfirmDialog, showInfoDialog, opts = {}) {
        this.model = model
        this.metadata = metadata
        this.showConfirmDialog = showConfirmDialog
        this.showInfoDialog = showInfoDialog
        this._projectManager = opts.projectManager || null
    }

    /**
     * Sorgt nach Import/Load fuer einen konsistenten Projekttitel:
     * - Wenn project.title === 'untitled': `proposal` wird in das Projekt
     *   geschrieben (renameProject + Sync-Triggers) und in metadata.title
     *   gespiegelt. Hintergrund: Pane-Border + Tab-Leiste + Dashboard sollen
     *   denselben Namen zeigen, und 'untitled' ist der Default-Trigger fuer
     *   eine Adoption.
     * - Wenn project.title bereits gesetzt ist: `proposal` wird ignoriert,
     *   und metadata.title wird auf project.title restauriert (sonst koennte
     *   ein vorheriger applyFrontmatter-Pfad den metadata-Schatten verschoben
     *   haben). Damit greift die Boris-Regel "Projektname ist fix, aenderbar
     *   nur ueber das Project Dashboard."
     *
     * @param {string} proposal - Vorgeschlagener Titel (z.B. Frontmatter-
     *   Title, .cbl-Header-Title oder Dateiname). Leer/null = keine Adoption.
     * @returns {Promise<boolean>} true wenn der Titel uebernommen wurde.
     */
    async _syncProjectTitle(proposal) {
        if (!this._projectManager) return false
        const current = this._projectManager.getCurrentProject?.()
        if (!current) return false

        if (current.title === 'untitled' && proposal) {
            // renameProject triggert den setOnRenamed-Hook in main.js, der
            // metadata.title + TabBar + Border konsistent aktualisiert.
            await this._projectManager.renameProject(current.id, proposal)
            return true
        }

        // Keine Adoption: metadata.title auf project.title zwingen, falls
        // loadFromHeader oder applyFrontmatter den Schatten verschoben haben.
        this.metadata.setTitle(current.title || '')
        return false
    }

    // ===== Hilfsfunktionen =====

    /**
     * Prüft ob das Dokument "dirty" ist (ungespeicherte Änderungen).
     * @returns {boolean}
     */
    isDirty() {
        // Dirty wenn: Undo-Stack nicht leer ODER Text vorhanden
        const hasContent = this.model.paragraphs.some(p => p.text.length > 0)
        const hasUndo = this.model.undoStack.length > 0
        return hasContent || hasUndo
    }

    /**
     * Triggert einen File-Download.
     * @param {string} content - Dateiinhalt
     * @param {string} filename - Dateiname
     * @param {string} mimeType - MIME-Type
     */
    _downloadFile(content, filename, mimeType) {
        const blob = new Blob([content], { type: mimeType })
        const url = URL.createObjectURL(blob)

        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)

        URL.revokeObjectURL(url)
    }

    /**
     * Öffnet einen File-Upload-Dialog.
     * @param {string} accept - Akzeptierte Dateitypen (z.B. '.txt,.md')
     * @returns {Promise<{name: string, content: string}|null>}
     */
    _uploadFile(accept) {
        return new Promise((resolve) => {
            const input = document.createElement('input')
            input.type = 'file'
            input.accept = accept

            input.onchange = async (e) => {
                const file = e.target.files[0]
                if (!file) {
                    resolve(null)
                    return
                }

                try {
                    const content = await this._readFileWithEncoding(file)
                    resolve({ name: file.name, content })
                } catch (err) {
                    resolve(null)
                }
            }

            input.oncancel = () => resolve(null)
            input.click()
        })
    }

    /**
     * Liest eine Datei mit automatischer Encoding-Erkennung.
     * Erkennt BOM für UTF-16 LE/BE und UTF-8, Fallback auf UTF-8.
     * @param {File} file
     * @returns {Promise<string>}
     */
    async _readFileWithEncoding(file) {
        const buffer = await file.arrayBuffer()
        const bytes = new Uint8Array(buffer)

        // BOM-Erkennung für korrektes Encoding
        let encoding = 'utf-8'
        if (bytes.length >= 2) {
            if (bytes[0] === 0xFF && bytes[1] === 0xFE) {
                encoding = 'utf-16le'
            } else if (bytes[0] === 0xFE && bytes[1] === 0xFF) {
                encoding = 'utf-16be'
            }
        }

        const decoder = new TextDecoder(encoding)
        return decoder.decode(buffer)
    }

    /**
     * Extrahiert die Dateiendung.
     * @param {string} filename
     * @returns {string} Lowercase Extension ohne Punkt
     */
    _getExtension(filename) {
        const parts = filename.split('.')
        if (parts.length < 2) return ''
        return parts[parts.length - 1].toLowerCase()
    }

    // ===== Clear Content =====

    /**
     * Leert den Text-Inhalt des aktuellen Projekts. Projektname und Author
     * bleiben unangetastet — der Projektname ist fix und nur ueber das
     * Project Dashboard aenderbar (Boris-Regel, 2026-05-12).
     *
     * Fragt nach Bestaetigung, wenn Inhalt vorhanden ist.
     * @returns {Promise<boolean>} true wenn erfolgreich
     */
    async handleClearContent() {
        // Prüfen ob Bestätigung nötig
        if (this.isDirty()) {
            const confirmed = await this.showConfirmDialog(
                'Clear Content',
                'The complete document content will be deleted.',
                'Continue?'
            )
            if (!confirmed) return false
        }

        // Editor leeren (setText resettet auch Undo/Redo-Stacks).
        // metadata.title/author/created bleiben — sie gehoeren zum Projekt,
        // nicht zum aktuellen Inhalt.
        this.model.setText('')

        return true
    }

    // ===== Import =====

    /**
     * Importiert eine .txt oder .md Datei.
     * @returns {Promise<boolean>} true wenn erfolgreich
     */
    async handleImport() {
        // Prüfen ob Bestätigung nötig
        if (this.isDirty()) {
            const confirmed = await this.showConfirmDialog(
                'Import File',
                'The complete document content will be deleted.',
                'Continue?'
            )
            if (!confirmed) return false
        }

        // Datei auswählen
        const file = await this._uploadFile('.txt,.md')
        if (!file) return false

        const ext = this._getExtension(file.name)

        // Format prüfen
        if (ext !== 'txt' && ext !== 'md') {
            await this.showInfoDialog(
                'Import Failed',
                `Unsupported format: .${ext}`,
                'Supported: .txt, .md'
            )
            return false
        }

        // Import durchführen
        const result = this._parseImport(file.content, ext)

        if (!result.success) {
            await this.showInfoDialog(
                'Import Failed',
                result.error,
                ''
            )
            return false
        }

        // In Editor laden (setText resettet auch Undo/Redo-Stacks)
        this.model.setText(result.text)

        // Cleanup-on-Leave-Regeln direkt nach dem Import auf alle Absaetze
        // anwenden — sonst muerbt der User beim ersten Pfeil-Durchwandern
        // Zeile fuer Zeile die Whitespace-/Punctuation-Bereinigung an, was
        // den Absatz dirty markiert und einen Cloud-Sync triggert. Cleanup
        // ueberspringt Separatoren und Code-Block-Absaetze von sich aus.
        for (const p of this.model.paragraphs) {
            EditorCleanup.cleanupParagraph(this.model, p.id)
        }

        // Author + created aus Frontmatter uebernehmen, falls vorhanden.
        // Title geht ueber den separaten _adoptTitleIfUntitled-Pfad — der
        // schreibt nur, wenn das Projekt noch 'untitled' heisst (Boris-Regel).
        if (result.metadata) {
            if (result.metadata.author) this.metadata.setAuthor(result.metadata.author)
            if (result.metadata.date || result.metadata.created) {
                this.metadata.created = result.metadata.date || result.metadata.created
            }
        }

        // Titel-Vorschlag: Frontmatter-Title > Dateiname (ohne Endung).
        // Nur uebernommen, wenn project.title === 'untitled'.
        const frontmatterTitle = result.metadata && result.metadata.title
        const baseName = file.name.replace(/\.[^.]+$/, '')
        const titleProposal = frontmatterTitle || baseName
        await this._syncProjectTitle(titleProposal)

        // Sofort flushen — saveContent() laeuft sonst debounced im Render-
        // Loop (1 s) und der grosse 1MB-Write danach. Folge: Dashboard und
        // Project Statistics zeigen direkt nach dem Erfolgs-Dialog noch die
        // ALTE sizeBytes-Schaetzung. Wir bridgen Model → ProjectManager
        // analog zu saveContent(): markAllDirty + markStructuralChange,
        // dann flushNow().
        if (this._projectManager) {
            const ids = this.model.paragraphs.map(p => p.id)
            this._projectManager.markAllDirty(ids)
            this._projectManager.markStructuralChange()
            try { await this._projectManager.flushNow() }
            catch (err) { console.warn('Import-Flush fehlgeschlagen:', err.message) }
        }

        // Ergebnis-Dialog
        await this.showInfoDialog(
            'Import Successful',
            `${result.paragraphs} paragraphs imported`,
            result.notes.length > 0 ? result.notes.join(', ') : ''
        )

        return true
    }

    /**
     * Parst importierten Text.
     * @param {string} content - Dateiinhalt
     * @param {string} ext - Dateiendung
     * @returns {Object} Parse-Ergebnis
     */
    _parseImport(content, ext) {
        const result = {
            success: false,
            text: '',
            paragraphs: 0,
            metadata: null,
            notes: [],
            error: null
        }

        try {
            let text = content

            // Bei Markdown: Frontmatter parsen
            if (ext === 'md') {
                const parsed = this.metadata.parseYamlFrontmatter(content)
                text = parsed.content
                if (Object.keys(parsed.metadata).length > 0) {
                    result.metadata = parsed.metadata
                    result.notes.push('Frontmatter parsed')
                }
            }

            // Validierung: Ist der Text lesbar?
            const validation = this._isValidText(text)
            if (!validation.valid) {
                result.error = validation.reason
                return result
            }
            if (validation.note) {
                result.notes.push(validation.note)
            }

            // Text normalisieren (entfernt BOM, Steuerzeichen, vereinheitlicht Zeilenenden)
            text = this._normalizeText(text)

            result.text = text
            result.paragraphs = text.split('\n').length
            result.success = true

            // Notizen zu erkannten Formaten
            if (text.includes('**') || text.includes('*')) {
                result.notes.push('Markdown formatting detected')
            }

        } catch (err) {
            result.error = `Parse error: ${err.message}`
        }

        return result
    }

    /**
     * Prüft ob der Text lesbar ist.
     * Wenige Null-Bytes oder Steuerzeichen werden toleriert (note statt Fehler).
     * @param {string} text
     * @returns {{valid: boolean, reason: string, note: string}}
     */
    _isValidText(text) {
        if (!text || text.length === 0) {
            return { valid: false, reason: 'File is empty', note: '' }
        }

        // Nicht-druckbare Zeichen zählen (Null-Bytes + Steuerzeichen, ohne Tab/Newline/CR)
        const nonPrintable = (text.match(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g) || []).length
        const ratio = nonPrintable / text.length

        // Hoher Anteil: tatsächlich binär
        if (ratio > 0.1) {
            return {
                valid: false,
                reason: `File appears to be binary (${(ratio * 100).toFixed(0)}% non-printable). Try saving as UTF-8.`,
                note: ''
            }
        }

        // Wenige non-printable: tolerieren, aber als Note melden
        const note = nonPrintable > 0
            ? `${nonPrintable} non-printable character${nonPrintable > 1 ? 's' : ''} removed`
            : ''

        return { valid: true, reason: '', note }
    }

    /**
     * Normalisiert Text (Zeilenenden, Whitespace).
     * @param {string} text
     * @returns {string}
     */
    _normalizeText(text) {
        return text
            .replace(/^\uFEFF/, '')                 // BOM entfernen (UTF-8/UTF-16)
            .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '')  // Steuerzeichen entfernen
            .replace(/\r\n/g, '\n')                 // Windows-Zeilenenden
            .replace(/\r/g, '\n')                   // Mac-Classic-Zeilenenden
            .replace(/\t/g, '  ')                   // Tabs zu Spaces
            .trimEnd()                              // Trailing Whitespace
    }

    // ===== Load (.cbl) =====

    /**
     * Lädt eine .cbl Datei.
     * @returns {Promise<boolean>} true wenn erfolgreich
     */
    async handleLoad() {
        // Prüfen ob Bestätigung nötig
        if (this.isDirty()) {
            const confirmed = await this.showConfirmDialog(
                'Load File',
                'The complete document content will be deleted.',
                'Continue?'
            )
            if (!confirmed) return false
        }

        // Datei auswählen
        const file = await this._uploadFile('.cbl')
        if (!file) return false

        const ext = this._getExtension(file.name)

        // Format prüfen
        if (ext !== 'cbl') {
            await this.showInfoDialog(
                'Load Failed',
                `Wrong format: .${ext}`,
                'Use Import for .txt/.md files'
            )
            return false
        }

        // JSON parsen
        let data
        try {
            data = JSON.parse(file.content)
        } catch (err) {
            await this.showInfoDialog(
                'Load Failed',
                'Invalid file format',
                'File is not valid JSON'
            )
            return false
        }

        // Struktur validieren
        const validation = this._validateCblFormat(data)
        if (!validation.valid) {
            await this.showInfoDialog(
                'Load Failed',
                validation.error,
                'Use Import for other formats'
            )
            return false
        }

        // Versions-Warnung
        if (validation.warning) {
            await this.showInfoDialog(
                'Warning',
                validation.warning,
                'Loading anyway...'
            )
        }

        // In Editor laden — mit frischen UUIDs und remappten Section-Links.
        // Paragraph-IDs sind global eindeutig (IDB Primary Key + server-seitig).
        // Ein CBL-Load in ein anderes Projekt (lokal oder serverseitig) darf
        // deshalb niemals die Original-IDs behalten, sonst kollidieren sie
        // mit bestehenden Paragraphen aus anderen Projekten.
        const { paragraphs } = remapParagraphIds(data.document.paragraphs)
        this.model.setParagraphsRT(paragraphs)
        // Section Lock für ersten Abschnitt wiederherstellen
        this.model._firstSectionLocked = !!data.document.firstSectionLocked

        // Metadaten aus Header laden — setzt auch metadata.title, aber
        // _syncProjectTitle unten korrigiert das je nach project.title:
        // entweder Adoption (untitled-Projekt) oder Restore auf project.title.
        this.metadata.loadFromHeader(data.carbisdale)

        const headerTitle = data.carbisdale && data.carbisdale.title
        await this._syncProjectTitle(headerTitle)

        return true
    }

    /**
     * Validiert das .cbl Format.
     * @param {Object} data - Geparste JSON-Daten
     * @returns {{valid: boolean, error?: string, warning?: string}}
     */
    _validateCblFormat(data) {
        // Prüfen auf carbisdale-Header
        if (!data.carbisdale) {
            return { valid: false, error: 'Not a Carbisdale file (missing header)' }
        }

        // Prüfen auf document-Sektion
        if (!data.document || !Array.isArray(data.document.paragraphs)) {
            return { valid: false, error: 'Invalid file structure (missing document)' }
        }

        // Versions-Prüfung: nur 4.x ist unterstuetzt. Aeltere Formate
        // (1.x/2.x/3.x) muessten in einer aelteren App-Version geoeffnet und
        // neu gespeichert werden.
        const fileVersion = data.carbisdale.formatVersion || '0.0'
        if (this._compareVersions(fileVersion, '4.0') < 0) {
            return {
                valid: false,
                error: `Unsupported file format (version ${fileVersion}). ` +
                    `This file uses an old format — open it in an earlier app version ` +
                    `and re-save before importing here.`
            }
        }

        let warning = null
        if (this._compareVersions(fileVersion, FORMAT_VERSION) > 0) {
            warning = `File version (${fileVersion}) is newer than app (${FORMAT_VERSION})`
        }

        return { valid: true, warning }
    }

    /**
     * Vergleicht zwei Versionsnummern.
     * @param {string} v1
     * @param {string} v2
     * @returns {number} -1, 0, oder 1
     */
    _compareVersions(v1, v2) {
        const parts1 = v1.split('.').map(Number)
        const parts2 = v2.split('.').map(Number)

        for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
            const p1 = parts1[i] || 0
            const p2 = parts2[i] || 0
            if (p1 < p2) return -1
            if (p1 > p2) return 1
        }
        return 0
    }

    // ===== Save (.cbl) =====

    /**
     * Speichert als .cbl Datei.
     * @returns {Promise<boolean>} true wenn erfolgreich
     */
    async handleSave() {
        // Header erstellen (setzt auch Timestamps)
        const header = this.metadata.createHeader()

        const document = {
            paragraphs: this.model.paragraphs.map(p => {
                // RT-Modus: natives Format mit Formats/Links/Block-Metadaten
                return serializeParagraphRT(p)
            })
        }
        // Section Lock für ersten Abschnitt (ohne Separator)
        if (this.model._firstSectionLocked) {
            document.firstSectionLocked = true
        }

        // Komplette Datei
        const data = {
            carbisdale: header,
            document: document
        }

        // JSON formatieren (lesbar)
        const json = JSON.stringify(data, null, 2)

        // Dateiname generieren
        const filename = this.metadata.generateFilename(this.model, 'cbl')

        // Download triggern
        this._downloadFile(json, filename, 'application/json')

        return true
    }

    // ===== Export (.md) =====

    /**
     * Exportiert als Markdown-Datei.
     * @returns {Promise<boolean>} true wenn erfolgreich
     */
    async handleExport() {
        // Markdown generieren
        let markdown = ''

        // Optional: Frontmatter hinzufügen
        const title = this.metadata.getTitle()
        const author = this.metadata.getAuthor()

        if (title || author) {
            markdown += '---\n'
            if (title) markdown += `title: "${title}"\n`
            if (author) markdown += `author: "${author}"\n`
            markdown += '---\n\n'
        }

        // Text hinzufügen (Links zu Klartext-Labels aufgelöst)
        markdown += this.model.getExportText()

        // Dateiname generieren
        const filename = this.metadata.generateFilename(this.model, 'md')

        // Download triggern
        this._downloadFile(markdown, filename, 'text/markdown')

        return true
    }
}
